import JsBarcode from 'jsbarcode';

// Add type declarations
type FormatType = 'date' | 'currency' | 'barcode' | 'qrcode';

interface FormatMatch {
  format: FormatType;
  varName: string;
}

// Barcode utility function
export const generateBarcodePreview = (text: string, options: any = {}): string => {
  try {
    const format = options.format || 'CODE128';
    const width = parseInt(options.width) || 2;
    const height = parseInt(options.height) || 100;
    const displayValue = options.displayValue !== undefined ? options.displayValue : true;

    // Create a temporary canvas element for preview generation
    const canvas = document.createElement('canvas');
    
    JsBarcode(canvas, text, {
      format: format,
      width: width,
      height: height,
      displayValue: displayValue,
      fontSize: 14,
      margin: 10,
      background: '#ffffff',
      lineColor: '#000000'
    });

    // Convert canvas to SVG-like representation for preview
    const svgWidth = canvas.width;
    const svgHeight = canvas.height;
    
    // Generate a proper barcode preview using canvas data
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Failed to get canvas context');
    }

    // Convert to data URL and then to SVG representation
    const dataUrl = canvas.toDataURL();
    
    return `<svg width="${svgWidth}" height="${svgHeight}" xmlns="http://www.w3.org/2000/svg">
      <foreignObject width="100%" height="100%">
        <img src="${dataUrl}" width="${svgWidth}" height="${svgHeight}" style="display: block;" />
      </foreignObject>
    </svg>`;
  } catch (error) {
    console.error('Barcode preview generation failed:', error);
    // Fallback to simple barcode preview
    return `<svg width="200" height="50" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="2" height="40" fill="black"/>
      <rect x="4" y="0" width="1" height="40" fill="black"/>
      <rect x="7" y="0" width="3" height="40" fill="black"/>
      <rect x="12" y="0" width="1" height="40" fill="black"/>
      <rect x="15" y="0" width="2" height="40" fill="black"/>
      <rect x="19" y="0" width="1" height="40" fill="black"/>
      <rect x="22" y="0" width="3" height="40" fill="black"/>
      <rect x="27" y="0" width="2" height="40" fill="black"/>
      <rect x="31" y="0" width="1" height="40" fill="black"/>
      <rect x="34" y="0" width="2" height="40" fill="black"/>
      <text x="100" y="55" text-anchor="middle" font-family="monospace" font-size="12" fill="black">${text}</text>
    </svg>`;
  }
};

export const replaceVariablesWithValues = (content: string, jsonData: string): string => {
  try {
    const data = JSON.parse(jsonData);
    
    // Function to get nested object value using dot notation
    const getNestedValue = (obj: any, path: string): any => {
      return path.split('.').reduce((prev, curr) => {
        return prev ? prev[curr] : undefined;
      }, obj);
    };

    // Replace all {{variable}} occurrences with their values
    return content.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
      // Remove any whitespace
      const cleanVariable = variable.trim();
      
      // Check if it's a formatted variable (date, currency, etc.)
      const formatMatch = cleanVariable.match(/^(date|currency|barcode|qrcode)\s+(.+?)(?:\s+(.*?))?$/);
      
      if (formatMatch) {
        const [, format, varName, optionsStr] = formatMatch;
        const value = getNestedValue(data, varName);
        
        if (value !== undefined) {
          switch (format as FormatType) {
            case 'date':
              return new Date(value).toLocaleDateString();
            case 'currency':
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
              }).format(value);
            case 'barcode':
              // Parse barcode options if provided
              const barcodeOptions: any = {};
              if (optionsStr) {
                // Parse options like: format="CODE128" width="2" height="100"
                const optionsRegex = /(\w+)="([^"]+)"/g;
                let optionMatch;
                while ((optionMatch = optionsRegex.exec(optionsStr)) !== null) {
                  barcodeOptions[optionMatch[1]] = optionMatch[2];
                }
              }
              return generateBarcodePreview(value.toString(), barcodeOptions);
            case 'qrcode':
              return value; // QR code generation is handled elsewhere
            default:
              return value;
          }
        }
      } else {
        const value = getNestedValue(data, cleanVariable);
        return value !== undefined ? value : match;
      }
      
      return match; // Keep original if no value found
    });
  } catch (error) {
    console.error('Error replacing variables:', error);
    return content;
  }
}; 